import { DesktopOutlined, MessageOutlined } from "@ant-design/icons"
import { Badge, Button, Drawer, Flex, Grid, Tag, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { type DesktopPermissionState } from "../../hooks/use-desktop-permission.ts"
import { SmsAlertsNotice } from "../../sms/SmsAlertsNotice.tsx"
import { type SmsVerificationState } from "../../sms/sms-verification.services.ts"
import { type SmsLock } from "../../sms/use-sms-verification.ts"

const { Text } = Typography

export interface SmsChannelState {
  // Whether any row on this page can go by text at all; with none, SMS has
  // nothing to set up here.
  offered: boolean
  state: SmsVerificationState | null
  lock: SmsLock
  failed: boolean
  onRetry: () => void
  onVerified: (state: SmsVerificationState) => void
}

export function smsNeedsSetup(sms: SmsChannelState) {
  if (!sms.offered) return false
  if (!sms.state && sms.failed) return true
  return sms.lock === "unverified" || sms.lock === "stopped"
}

export function desktopNeedsSetup(desktop: DesktopPermissionState) {
  return (
    desktop.notYetAllowed || (desktop.wanted && desktop.permission === "denied")
  )
}

function ChannelSection({
  icon,
  title,
  status,
  description,
  children
}: {
  icon: React.ReactNode
  title: string
  status?: React.ReactNode
  description: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-2 py-5 border-t border-border">
      <Flex align="center" justify="space-between" gap={8}>
        <span className="inline-flex items-center gap-3">
          <span aria-hidden="true" className="text-xl text-primary">
            {icon}
          </span>
          <Text strong className="text-base">
            {title}
          </Text>
        </span>
        {status}
      </Flex>
      <Text className="text-gray-700">{description}</Text>
      {children}
    </section>
  )
}

function DesktopSection({ desktop }: { desktop: DesktopPermissionState }) {
  const { t } = useTranslation()
  const { permission } = desktop

  const status =
    permission === "granted" ? (
      <Tag color="green">{t("notifications.settings.channelsAllowed")}</Tag>
    ) : permission === "default" ? (
      <Tag>{t("notifications.settings.channelsNotYetAllowed")}</Tag>
    ) : permission === "denied" ? (
      <Tag color="orange">{t("notifications.settings.channelsBlocked")}</Tag>
    ) : (
      <Tag>{t("notifications.settings.channelsUnsupported")}</Tag>
    )

  return (
    <ChannelSection
      icon={<DesktopOutlined />}
      title={t("notifications.channels.desktop")}
      status={status}
      description={t("notifications.settings.desktopDescription")}
    >
      <Text type="secondary">
        {t("notifications.settings.desktopTableHint")}
      </Text>
      {permission === "unsupported" && (
        <Text type="secondary">
          {t("notifications.settings.desktopUnsupported")}
        </Text>
      )}
      {permission === "denied" && (
        <Text type="secondary">
          {t("notifications.settings.desktopBlocked")}
        </Text>
      )}
      {permission === "default" && (
        <Flex vertical gap={8} align="start">
          {desktop.wanted && (
            <Text type="secondary">
              {t("notifications.settings.desktopNotYetAllowed")}
            </Text>
          )}
          <Button size="small" onClick={() => void desktop.ask()}>
            {t("notifications.settings.allowDesktop")}
          </Button>
        </Flex>
      )}
    </ChannelSection>
  )
}

function SmsSection({ sms }: { sms: SmsChannelState }) {
  const { t } = useTranslation()
  const unavailable = sms.state !== null && !sms.state.available
  const status = !sms.state ? null : unavailable ? (
    <Tag>{t("notifications.settings.channelsUnavailable")}</Tag>
  ) : sms.state.stopped ? (
    <Tag color="orange">{t("notifications.settings.channelsStopped")}</Tag>
  ) : sms.state.verified ? (
    <Tag color="green">{t("smsVerification.verified")}</Tag>
  ) : (
    <Tag>{t("smsVerification.notVerified")}</Tag>
  )

  return (
    <ChannelSection
      icon={<MessageOutlined />}
      title={t("notifications.channels.sms")}
      status={status}
      description={t("notifications.settings.smsDescription")}
    >
      {unavailable && (
        <Text type="secondary">{t("notifications.settings.smsNotYet")}</Text>
      )}
      {/* Verification is offered only where a notification here can go by
          text, as the page banner it replaces was. */}
      {sms.offered && (
        <SmsAlertsNotice
          state={sms.state}
          lock={sms.lock}
          failed={sms.failed}
          onRetry={sms.onRetry}
          onVerified={sms.onVerified}
        />
      )}
    </ChannelSection>
  )
}

interface ManageChannelsProps {
  desktop: DesktopPermissionState
  sms: SmsChannelState
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** The channels on this page that need something from this browser or this
 *  person before they can deliver, and the existing flows that provide it. */
export function ManageChannels({
  desktop,
  sms,
  open,
  onOpenChange
}: ManageChannelsProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const needsSetup = smsNeedsSetup(sms) || desktopNeedsSetup(desktop)

  return (
    <>
      <div className="flex flex-col items-start sm:items-end gap-1.5">
        <Badge dot={needsSetup} color="orange" offset={[-4, 4]}>
          <Button
            onClick={() => onOpenChange(true)}
            aria-haspopup="dialog"
            aria-label={
              needsSetup
                ? t("notifications.settings.manageChannelsNeedsSetup")
                : t("notifications.settings.manageChannels")
            }
            className="border-primary text-primary"
          >
            {t("notifications.settings.manageChannels")}
          </Button>
        </Badge>
        <Text type="secondary" className="text-xs">
          {t("notifications.settings.manageChannelsHelper")}
        </Text>
      </div>
      <Drawer
        open={open}
        onClose={() => onOpenChange(false)}
        title={t("notifications.settings.manageChannels")}
        width={screens.md ? 420 : "100%"}
        destroyOnClose={false}
      >
        <div className="mb-4">
          <Text type="secondary">
            {t("notifications.settings.manageChannelsIntro")}
          </Text>
        </div>
        <DesktopSection desktop={desktop} />
        <SmsSection sms={sms} />
      </Drawer>
    </>
  )
}
